import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useOfferEventSuggestionsMutation, useUpdateEventSuggestionMutation } from '@graphql/generated';
import { useDisclosure } from '@withjoy/joykit';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useTranslation } from '@shared/core';

const PAYMENT_METHOD_DISCLAIMER_SIGNED = 'paymentMethodDisclaimerSignedList';
export interface PaymentMethodDisclaimerSignedMetadata {
  paymentMethod: string;
  paymentUsername: string;
  paymentLink: string;
  timestamp: number;
  userId: string;
}

export function useAppPaymentDisclaimerDialogController(eventId: string) {
  const [offerEventSuggestionsMutation, { data, loading }] = useOfferEventSuggestionsMutation();
  const paymentMethodsDisclaimerSigned = useMemo(() => data?.offerEventSuggestions.at(0), [data]);
  const [updateEventSuggestionMutation] = useUpdateEventSuggestionMutation();
  const [paymentMetadata, setPaymentMetadata] = useState<PaymentMethodDisclaimerSignedMetadata | null>(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const telemetry = useAdminRegistryTelemetry();
  const { t } = useTranslation('generalFormValidation');

  const disclaimerFormik = useFormik<{ agreementChecked: boolean; trackingChecked: boolean }>({
    initialValues: { agreementChecked: false, trackingChecked: false },
    onSubmit: async (values, { resetForm }) => {
      if (!paymentMethodsDisclaimerSigned?.id || loading) return;
      telemetry.acceptCashFundDisclaimerClicked({
        paymentLink: paymentMetadata?.paymentLink || '',
        paymentMethod: paymentMetadata?.paymentMethod || '',
        paymentUsername: paymentMetadata?.paymentUsername || '',
      });

      await updateEventSuggestionMutation({
        variables: {
          id: paymentMethodsDisclaimerSigned.id,
          payload: {
            stateJSON: { paymentMethodsDisclaimerSignedList: paymentMetadata },
          },
        },
      });
      setAgreementAccepted(true);
      resetForm();
      onClose();
    },
    validationSchema: Yup.object().shape({
      agreementChecked: Yup.boolean().oneOf([true], t('mustAgreeToContinue')()),
      trackingChecked: Yup.boolean().oneOf([true], t('mustAgreeToContinue')()),
    }),
  });

  const { onOpen, onClose, isOpen } = useDisclosure();

  useEffect(() => {
    offerEventSuggestionsMutation({
      variables: {
        eventId,
        identifiers: [PAYMENT_METHOD_DISCLAIMER_SIGNED],
      },
    });
  }, [offerEventSuggestionsMutation, eventId]);

  const onOpenDisclaimerDialog = (paymentMetadata: PaymentMethodDisclaimerSignedMetadata) => {
    setPaymentMetadata(paymentMetadata);
    onOpen();
  };

  return { onOpenDisclaimerDialog, onClose, isOpen, disclaimerFormik, agreementAccepted, setAgreementAccepted, paymentMetadata };
}
